import { API_BASE_URL, ENDPOINTS } from "@/config/api.enpoints";
import apiClient from "./apiClient";
import type {
  AddUserPayload,
  CreateUserPayload,
  FetchUsersParams,
  UsageLogsApiResponse,
  UsageLogsPayload,
} from "@/types/user";
import type {
  Feature,
  ScriptCardDto,
  ScriptGeneration,
  ScriptGenResponse,
  TenantProfile,
} from "@/types/tenantService";
import type { CreditUsageParams } from "@/types/creditTypes";
interface TenantItem {
  id: string;
  name: string;
  domain: string | null;
  type: "COMPANY" | "INDIVIDUAL";
  planMasterId: string;
  createdAt: string;
  updatedAt: string;
  status: "active" | "inactive";
  website_url: string;
  linkedIn_url: string;
  logoUrl?: string | null;
  tenantProfile?: TenantProfile;
  usage_log: UsageLog[];
  planMaster: PlanMaster;
  contactEmail: string;
}
export interface UsageLog {
  id: string;
  tenantId: string;
  featureId: string;
  userId: string;
  referenceId: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  log_type: string;
  created_at: string;
}
export interface PlanMaster {
  planMasterId: string;
  id: string;
  name: string;
  displayName: string;
  description: string;
  price: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  licenselimit: number;
  tokenlimit: number;
}

export type TenantDashboardOverviewParams = {
  page?: number;
  pageSize?: number;
  orderBy?: string;
};

export const fetchUsers = async ({
  page = 1,
  pageSize = 10,
  search = "",
  role = "",
  tenantId = "",
  orderBy,
  order,
}: FetchUsersParams & { tenantId?: string } = {}) => {
  const params: Record<string, string | number> = {
    page,
    pageSize,
  };

  if (search?.trim()) {
    params.searchKeyword = search;
    params.search = search;
  }

  if (role?.trim()) {
    params.role = role;
  }

  if (orderBy) params.orderBy = orderBy;
  if (order) params.order = order;

  // Add tenantId if passed for superadmin
  if (tenantId?.trim()) {
    params.tenantId = tenantId;
  }

  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.TENANT.users}`,
    {
      params,
    },
  );

  return response.data;
};

export const fetchPitchCount = async () => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.TENANT.pitchCount}`,
  );
  return response.data;
};

export const addUser = async (payload: AddUserPayload) => {
  const response = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.ADD_USER.register}`,
    payload,
  );
  return response.data;
};

export const createUser = async (payload: CreateUserPayload) => {
  const response = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.UPDATE_USER.create}`,
    payload,
  );
  return response.data;
};

export const deleteTenant = async (
  tenantId: string,
): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete<{ message: string }>(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.getActiveTenants}/${tenantId}`,
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error((error as Error).message || "Failed to delete tenant");
  }
};

export interface UpdateProfileResponse {
  message: string;
  user: {
    id: string;
    name: string;
    email: string;
    tenantType: "INDIVIDUAL";
    companyName: string;
    status: string;
    phoneNumber?: string;
  };
}

export const updateUser = async (
  id: string,
  payload: FormData,
): Promise<UpdateProfileResponse> => {
  try {
    const res = await apiClient.put<UpdateProfileResponse>(
      `${API_BASE_URL}${ENDPOINTS.UPDATE_USER.updateUser(id)}`,
      payload,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return res.data;
  } catch (error: unknown) {
    throw new Error((error as Error).message || "Failed to update profile");
  }
};

export const getUsageLog = async (
  tenantId: string,
  payload: UsageLogsPayload & { page?: number; limit?: number },
): Promise<UsageLogsApiResponse> => {
  const { data } = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.ADMIN.usageLogsQueryKey(tenantId)}`,
    payload,
  );
  return data.data;
};

export const getTenantFeature = async (
  tenantId: string,
): Promise<Feature[]> => {
  const res = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.ADMIN.tenantFeatures(tenantId)}`,
  );
  return res.data.data;
};

export const getTenantById = async (
  tenantId: string,
  query: Record<string, string | number>,
): Promise<TenantItem | null> => {
  try {
    const stringQuery: Record<string, string> = Object.fromEntries(
      Object.entries(query).map(([key, value]) => [key, String(value)]),
    );
    const object = new URLSearchParams(stringQuery);
    const { data } = await apiClient.get<{ data: TenantItem }>(
      `${API_BASE_URL}${ENDPOINTS.ADMIN.getActiveTenants}/${tenantId}?${object}`,
    );
    return data?.data;
  } catch (error) {
    console.error(error);
    return null;
  }
};
export const getUserTenantById = async (id: string) => {
  if (id === "new") return null;
  const response = await apiClient(
    `${API_BASE_URL}${ENDPOINTS.TENANT.usersByID(id)}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    },
  );
  return response.data.data;
};

export const addTeam = async (payload: AddUserPayload) => {
  const response = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.TEAM.TEAM}`,
    payload,
  );
  return response.data;
};
export const scriptGeneration = async (payload: ScriptGeneration) => {
  const response = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.SCRIPT.scriptGeneration}`,
    payload,
  );
  return response.data;
};

export const generatePersonalizedScriptSections = async (payload: { userId?: string; scriptType: string; companyId?: string; companyContactId?: string; companyInsight?: string }) => {
  const response = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.SCRIPT.generatePersonalizedSections}`,
    payload,
  );
  return response.data;
};

export const fetchRepInsights = async (userId: string) => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.SCRIPT.repInsights(userId)}`
  );
  return response.data;
};

export const fetchCompanyInsights = async (companyId: string) => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.SCRIPT.companyInsights(companyId)}`
  );
  return response.data;
};

export const fetchTargetCompanies = async (params?: { userId?: string; tenantId?: string } | string) => {
  let url = `${API_BASE_URL}${ENDPOINTS.CUSTOMER.base}`;
  if (typeof params === "string") {
    url += `?tenantId=${params}`;
  } else if (params) {
    const query = new URLSearchParams();
    if (params.tenantId) query.append("tenantId", params.tenantId);
    if (params.userId) query.append("userId", params.userId);
    const qs = query.toString();
    if (qs) url += `?${qs}`;
  }
  const response = await apiClient.get(url);
  return response.data;
};

export const fetchCompanyContacts = async (customerId?: string) => {
  const url = customerId 
    ? `${API_BASE_URL}${ENDPOINTS.COMPANY_CONTACT.base}?customerId=${customerId}`
    : `${API_BASE_URL}${ENDPOINTS.COMPANY_CONTACT.base}`;
  const response = await apiClient.get(url);
  return response.data;
};

export const fetchScriptDashboardOverview = async (tenantId: string) => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.SCRIPT.scriptDashboardOverview(tenantId)}`
  );
  return response.data;
};

export const getTeam = async (page = 1, limit = 10, search = "") => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.TEAM.TEAM}?tenantId=${localStorage.getItem("tenantId")}&page=${page}&limit=${limit}&search=${search}`,
  );
  return response.data;
};

export const updateTeam = async (
  id: string,
  payload: {
    [key: string]: string | string[] | File | undefined;
    id?: string;
    name?: string;
    email?: string;
    phone?: string;
    role?: string;
    avatar?: File;
    team?: string;
    userId?: string;
  },
): Promise<UpdateProfileResponse> => {
  try {
    const res = await apiClient.post<UpdateProfileResponse>(
      `${API_BASE_URL}${ENDPOINTS.TEAM.TEAM_UPDATE(id)}`,
      payload,
    );
    return res.data;
  } catch (error: unknown) {
    throw new Error((error as Error).message);
  }
};
export const getTeams = async (tenantId: string) => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.TEAM.getAll}`,
    {
      params: { tenantId },
    },
  );
  return response.data;
};
export const deleteTeam = async (
  teamId: string | File | undefined,
  userId: string | undefined,
): Promise<UpdateProfileResponse> => {
  try {
    const teamIdStr =
      typeof teamId === "string"
        ? teamId
        : teamId instanceof File
          ? teamId.name
          : "";
    const userIdStr = userId ?? "";
    const res = await apiClient.delete<UpdateProfileResponse>(
      `${API_BASE_URL}${ENDPOINTS.TEAM.TEAM_DELETE(teamIdStr, userIdStr)}`,
    );
    return res.data;
  } catch (error: unknown) {
    throw new Error((error as Error).message);
  }
};
export const fetchScriptCardData = async (
  params: {
    scripttype?: string;
    assignedToId?: string;
    assignedById?: string;
    search?: string;
    page?: number;
    limit?: number;
  },
  tenantID?: string,
): Promise<{ data: { data: ScriptCardDto[]; total: number } }> => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.SCRIPT.scriptCardData}/${tenantID}`,
    { params },
  );
  return response.data;
};

export const fetchScriptDetails = async (id: string) => {
  const res = await apiClient.get<{ data: ScriptGenResponse }>(
    `${API_BASE_URL}${ENDPOINTS.SCRIPT.generatedScriptData}/${id}`,
  );
  return res.data.data;
};

export const updateScriptMeta = async (
  scriptMetaId: string,
  data: ScriptGeneration,
) => {
  const response = await apiClient.patch(
    `${API_BASE_URL}${ENDPOINTS.SCRIPT.generatedScriptData}/${scriptMetaId}`,
    data,
    {},
  );
  return response.data;
};
export const deleteScriptMeta = async (scriptMetaId: string) => {
  const response = await apiClient.delete(
    `${API_BASE_URL}${ENDPOINTS.SCRIPT.generatedScriptData}/${scriptMetaId}`,
  );
  return response.data;
};

export const deleteWholeTeam = async (
  teamId: string,
): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete<{ message: string }>(
      `${API_BASE_URL}${ENDPOINTS.TEAM.TEAM}/${teamId}`,
    );
    return response.data;
  } catch (error: unknown) {
    throw new Error((error as Error).message || "Failed to delete team");
  }
};

export const fetchtenantsCount = async () => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.ADMIN.getAllTenants}`,
  );
  return response.data;
};

export const fetchAdminUsers = async ({
  page = 1,
  pageSize = 10,
  search = "",
  tenantId = "",
  orderBy,
  order,
}: FetchUsersParams = {}) => {
  const params: Record<string, string | number> = {
    page,
    pageSize,
  };

  if (search?.trim()) {
    params.searchKeyword = search;
    params.search = search;
  }

  if (tenantId?.trim()) {
    params.tenantId = tenantId;
  }
  if (orderBy) params.orderBy = orderBy;
  if (order) params.order = order;

  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.ADMIN.tenantsUsers}`,
    {
      params,
    },
  );

  return response.data;
};

export const fetchDeletedUsers = async ({
  page = 1,
  pageSize = 10,
  search = "",
  orderBy,
  order,
}: FetchUsersParams = {}) => {
  const params: Record<string, string | number> = {
    page,
    pageSize,
  };

  if (search?.trim()) {
    params.search = search;
  }

  if (orderBy) params.orderBy = orderBy;
  if (order) params.order = order;

  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.UPDATE_USER.deleted}`,
    {
      params,
    },
  );

  return response.data;
};

export const fetchCreditUsage = async (params: CreditUsageParams) => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.CREDITS.usage}`,
    {
      params,
    },
  );
  return response.data;
};

export const grantCredits = async (payload: { targetType: 'USER' | 'TENANT'; targetId: string; amount: number; expiresAt?: string }) => {
  const response = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.CREDITS.grant}`,
    payload
  );
  return response.data;
};

export const splitGrantCredits = async (payload: { splitMode: 'FULL_POOL' | 'PER_USER'; amount?: number }) => {
  const response = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.CREDITS.splitGrant}`,
    payload
  );
  return response.data;
};

// ─── Rep Coaching Insights ─────────────────────────────────────────────────────
// POST: trigger generation
export const generateRepCoachingInsights = async (repId: string) => {
  const response = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.TENANT.repCoaching(repId)}`,
  );
  return response.data?.data ?? response.data;
};

// GET: fetch generated insights
export const getRepCoachingInsights = async (repId: string) => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.TENANT.repCoaching(repId)}`,
  );
  return response.data?.data ?? response.data;
};

// ─── Team Coaching Overview ────────────────────────────────────────────────────
export const getTeamCoachingOverview = async () => {
  const response = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.TENANT.teamCoachingOverview}`,
  );
  return response.data;
};

export const generateTeamCoachingInsights = async () => {
  const response = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.TENANT.generateTeamCoachingInsights}`,
  );
  return response.data;
};

export const getTenantDashboardOverview = async (
  params: TenantDashboardOverviewParams = {},
) => {
  const { data } = await apiClient.get(
    `${API_BASE_URL}${ENDPOINTS.TENANT.getTenantDashboardOverview}`,
    {
      params: {
        page: 1,
        pageSize: 1000,
        orderBy: 'desc',
        ...params,
      },
    },
  );
  return data;
};

export interface CheckScenarioMatchPayload {
  weakArea: string;
  recommendedTitle: string;
  tenantId: string;
  userId?: string;
}

export const checkScenarioMatch = async (payload: CheckScenarioMatchPayload) => {
  const response = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.TENANT.checkScenarioMatch}`,
    payload
  );
  return response.data;
};

export const fetchAllocations = async () => {
  const response = await apiClient.get(
    `${API_BASE_URL}/v1/credits/allocations`
  );
  return response.data;
};

export const updateAllocation = async (payload: { grantId: string; amount: number }) => {
  const response = await apiClient.put(
    `${API_BASE_URL}/v1/credits/allocations/${payload.grantId}`,
    { amount: payload.amount }
  );
  return response.data;
};

export const reassignScriptAssignment = async (
  assignmentId: string,
  assignedToId: string
) => {
  const response = await apiClient.post(
    `${API_BASE_URL}${ENDPOINTS.SCRIPT.reassignScript(assignmentId)}`,
    { assignedToId }
  );
  return response.data;
};
export const tenantUsageGraphData = async (tenantId: string, filters: { type: string; year?: number; userId?: string }) => {
  const response = await apiClient.get(
    `${API_BASE_URL}/v1/usage/tenant/${tenantId}/usage-count-graph`,
    { params: filters }
  );
  return response.data;
};
